import { AI_HELP_RESERVED_SPACE_EVENT, readAiHelpReservedSpacePx } from '@constants/layout'
import { Header } from '@containers/Header/Header'
import { useScreen } from '@utils'
import { AnimatePresence, useReducedMotion } from 'framer-motion'
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { FaXmark } from 'react-icons/fa6'
import {
  DanknessContainer,
  StyledColumnLayoutColumn,
  StyledColumnLayoutColumnContent,
  StyledColumnLayoutColumnContentWrapper,
  StyledFloatingDetailBody,
  StyledFloatingDetailHeader,
  StyledFloatingDetailCloseButton,
  StyledColumnLayoutOverlayBackdrop,
  StyledColumnLayoutColumnWrapper,
  StyledColumnLayoutWrapper,
  FLOATING_DETAIL_CARD_Z
} from './DoubleColumnLayout.styled'

const CARD_MOVEMENT = 16 // px
const CARD_SHADOW_ON = '0px 5px 15px 0px rgba(0, 0, 0, 0.05)'
const CARD_SHADOW_OFF = '0px 0px 0px 0px rgba(0, 0, 0, 0.05)'
const LEFT_COLUMN_MIN_WIDTH_REM = 20
const RIGHT_COLUMN_MIN_WIDTH_REM = 24
const FLOATING_CARD_INSET_PX = 16
const FLOATING_CARD_TOP_INSET_PX = 16
const FLOATING_CARD_MIN_WIDTH_PX = 384
const FLOATING_CARD_MAX_WIDTH_PX = 896
const FLOATING_CARD_WIDTH_RATIO = 0.72

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

type OverlayAnchorRect = {
  left: number
  top: number
  right: number
  width: number
  height: number
}

const readOverlayAnchorRect = (node: HTMLElement): OverlayAnchorRect => {
  const rect = node.getBoundingClientRect()

  return {
    left: rect.left,
    top: rect.top,
    right: rect.right,
    width: rect.width,
    height: rect.height
  }
}

const useReservedViewportSpacePx = () => {
  const [reservedSpacePx, setReservedSpacePx] = useState(0)

  useEffect(() => {
    const updateReservedSpace = () => {
      setReservedSpacePx(readAiHelpReservedSpacePx())
    }

    updateReservedSpace()
    window.addEventListener('resize', updateReservedSpace)
    window.addEventListener(AI_HELP_RESERVED_SPACE_EVENT, updateReservedSpace)

    return () => {
      window.removeEventListener('resize', updateReservedSpace)
      window.removeEventListener(AI_HELP_RESERVED_SPACE_EVENT, updateReservedSpace)
    }
  }, [])

  return reservedSpacePx
}

interface DoubleColumnLayoutProps {
  header: string
  /** When set, the top bar title links here; omit for a static title (list pages). */
  headerTo?: string
  isRightVisible?: boolean
  forceOverlayMode?: boolean
  onRightClose: () => void
  children: React.ReactNode
}

const LayoutContext = React.createContext({
  isVisible: false,
  isOverlay: false,
  overlayAnchorRect: null as OverlayAnchorRect | null,
  onClose: () => undefined,
  isMobile: false
})

interface FloatingDetailControlsContextValue {
  isOverlayCardVisible: boolean
  onClose: () => void
  registerCustomCloseButton: () => () => void
}

const FloatingDetailControlsContext =
  React.createContext<FloatingDetailControlsContextValue | null>(null)

export const useFloatingDetailControls = () => useContext(FloatingDetailControlsContext)

export const DoubleColumnLayout: React.FC<DoubleColumnLayoutProps> = ({
  header,
  headerTo,
  isRightVisible = false,
  forceOverlayMode = false,
  onRightClose,
  children
}) => {
  const { isMobile, width: viewportWidth, height: viewportHeight } = useScreen()
  const columnWrapperRef = useRef<HTMLDivElement>(null)
  const [isOverlay, setIsOverlay] = useState(false)
  const [overlayAnchorRect, setOverlayAnchorRect] = useState<OverlayAnchorRect | null>(null)

  const evaluateLayoutMode = useCallback(() => {
    const wrapperNode = columnWrapperRef.current
    if (!wrapperNode) return

    setOverlayAnchorRect(readOverlayAnchorRect(wrapperNode))

    const wrapperStyles = window.getComputedStyle(wrapperNode)
    const paddingLeft = parseFloat(wrapperStyles.paddingLeft || '0') || 0
    const paddingRight = parseFloat(wrapperStyles.paddingRight || '0') || 0
    const gap = parseFloat(wrapperStyles.columnGap || wrapperStyles.gap || '0') || 0
    const rootFontSize =
      parseFloat(window.getComputedStyle(document.documentElement).fontSize || '16') || 16

    const availableWidth = wrapperNode.clientWidth - paddingLeft - paddingRight
    const minimumRequiredWidth =
      (LEFT_COLUMN_MIN_WIDTH_REM + RIGHT_COLUMN_MIN_WIDTH_REM) * rootFontSize + gap

    setIsOverlay(forceOverlayMode || availableWidth < minimumRequiredWidth)
  }, [forceOverlayMode])

  useEffect(() => {
    evaluateLayoutMode()
  }, [evaluateLayoutMode, viewportWidth, viewportHeight, isMobile])

  useEffect(() => {
    const wrapperNode = columnWrapperRef.current
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            evaluateLayoutMode()
          })

    if (wrapperNode && observer) {
      observer.observe(wrapperNode)
    }

    const handleViewportChange = () => {
      evaluateLayoutMode()
    }

    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)
    window.addEventListener(AI_HELP_RESERVED_SPACE_EVENT, handleViewportChange)

    const visualViewport = window.visualViewport
    visualViewport?.addEventListener('resize', handleViewportChange)
    visualViewport?.addEventListener('scroll', handleViewportChange)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
      window.removeEventListener(AI_HELP_RESERVED_SPACE_EVENT, handleViewportChange)
      visualViewport?.removeEventListener('resize', handleViewportChange)
      visualViewport?.removeEventListener('scroll', handleViewportChange)
    }
  }, [evaluateLayoutMode])

  return (
    <DanknessContainer>
      <StyledColumnLayoutWrapper key={header} className="page-layout-wrapper">
        <Header label={header} labelTo={headerTo} />
        <LayoutContext.Provider
          value={{
            isVisible: isRightVisible,
            isOverlay,
            overlayAnchorRect,
            onClose: onRightClose,
            isMobile
          }}
        >
          <StyledColumnLayoutColumnWrapper ref={columnWrapperRef}>
            <AnimatePresence>
              {isOverlay && isRightVisible && (
                <StyledColumnLayoutOverlayBackdrop
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: 'easeInOut' }}
                  onClick={onRightClose}
                />
              )}
            </AnimatePresence>
            {children}
          </StyledColumnLayoutColumnWrapper>
        </LayoutContext.Provider>
      </StyledColumnLayoutWrapper>
    </DanknessContainer>
  )
}

interface DoubleLayoutColumnProps extends React.DetailedHTMLProps<any, HTMLElement> {
  innerKey: string
  isRight?: boolean
}

export const DoubleColumnLayoutColumn = React.forwardRef<any, DoubleLayoutColumnProps>(
  ({ innerKey, isRight = false, children, ...rest }, ref) => {
    const shouldReduceMotion = useReducedMotion()
    const [customCloseButtonRegistrations, setCustomCloseButtonRegistrations] = useState<
      Set<symbol>
    >(() => new Set())

    const shouldBeVisible = useCallback(
      (isVisible: boolean) => (isRight ? isVisible : true),
      [isRight]
    )
    const { isVisible, isOverlay, overlayAnchorRect, onClose, isMobile } = useContext(LayoutContext)
    const { width: viewportWidth, height: viewportHeight } = useScreen()
    const reservedSpacePx = useReservedViewportSpacePx()
    const isOverlayCardVisible = isRight && isOverlay && isVisible
    const floatingCardInsetPx = isMobile ? 0 : FLOATING_CARD_INSET_PX
    const floatingCardTopInsetPx = isMobile ? 0 : FLOATING_CARD_TOP_INSET_PX
    const floatingContentRef = useRef<HTMLDivElement | null>(null)
    const shouldRenderFallbackCloseButton =
      isOverlayCardVisible && customCloseButtonRegistrations.size === 0

    useEffect(() => {
      if (!isOverlayCardVisible) {
        setCustomCloseButtonRegistrations(new Set())
      }
    }, [isOverlayCardVisible])

    const floatingCardLayout = useMemo(() => {
      if (!isOverlayCardVisible || !overlayAnchorRect) {
        return null
      }

      const overlayZoneWidth = Math.max(overlayAnchorRect.width - floatingCardInsetPx * 2, 0)
      const overlayZoneHeight = Math.max(
        viewportHeight - floatingCardTopInsetPx - floatingCardInsetPx,
        0
      )
      const floatingCardWidthPx = Math.min(
        isMobile
          ? overlayZoneWidth
          : clamp(
              overlayZoneWidth * FLOATING_CARD_WIDTH_RATIO,
              FLOATING_CARD_MIN_WIDTH_PX,
              FLOATING_CARD_MAX_WIDTH_PX
            ),
        overlayZoneWidth
      )
      const floatingCardHeightPx = overlayZoneHeight
      // `overlayAnchorRect.right` is already measured in the current viewport coordinates.
      // If the main app area has been shrunk by reserved AI-help space, that offset is already
      // included in `(viewportWidth - overlayAnchorRect.right)`. Add only the inset here and
      // clamp to at least the reserved-space boundary to avoid double offsetting to the left.
      const floatingCardRightPx = Math.max(
        viewportWidth - overlayAnchorRect.right + floatingCardInsetPx,
        reservedSpacePx + floatingCardInsetPx
      )

      return {
        top: `${floatingCardTopInsetPx}px`,
        right: `${floatingCardRightPx}px`,
        left: 'auto',
        width: `${floatingCardWidthPx}px`,
        height: `${floatingCardHeightPx}px`,
        maxWidth: `${overlayZoneWidth}px`,
        maxHeight: `${overlayZoneHeight}px`,
        zIndex: FLOATING_DETAIL_CARD_Z
      } as const
    }, [
      floatingCardInsetPx,
      floatingCardTopInsetPx,
      isMobile,
      isOverlayCardVisible,
      overlayAnchorRect,
      reservedSpacePx,
      viewportHeight,
      viewportWidth
    ])

    useLayoutEffect(() => {
      const node = floatingContentRef.current
      if (!node || !floatingCardLayout) {
        return
      }

      node.style.top = floatingCardLayout.top
      node.style.right = floatingCardLayout.right
      node.style.left = floatingCardLayout.left
      node.style.width = floatingCardLayout.width
      node.style.height = floatingCardLayout.height
      node.style.maxWidth = floatingCardLayout.maxWidth
      node.style.maxHeight = floatingCardLayout.maxHeight
      node.style.zIndex = String(floatingCardLayout.zIndex)
    }, [floatingCardLayout])

    const registerCustomCloseButton = useCallback(() => {
      const registrationId = Symbol('floating-close-button')

      setCustomCloseButtonRegistrations(previousRegistrations => {
        const nextRegistrations = new Set(previousRegistrations)
        nextRegistrations.add(registrationId)
        return nextRegistrations
      })

      return () => {
        setCustomCloseButtonRegistrations(previousRegistrations => {
          if (!previousRegistrations.has(registrationId)) {
            return previousRegistrations
          }

          const nextRegistrations = new Set(previousRegistrations)
          nextRegistrations.delete(registrationId)
          return nextRegistrations
        })
      }
    }, [])

    const setContentRef = useCallback(
      (node: HTMLDivElement | null) => {
        floatingContentRef.current = node

        if (typeof ref === 'function') {
          ref(node)
        } else if (ref && 'current' in ref) {
          const forwardedRef = ref as React.MutableRefObject<any>
          forwardedRef.current = node
        }
      },
      [ref]
    )

    const animation = useMemo(() => {
      const amountOfMovement = shouldReduceMotion ? 0 : CARD_MOVEMENT
      const scaleIn = shouldReduceMotion ? 1 : 1.1
      const scaleOut = shouldReduceMotion ? 1 : 0.97

      return {
        variants: {
          initial: {
            opacity: 0,
            scale: scaleIn,
            y: amountOfMovement,
            boxShadow: CARD_SHADOW_OFF
          },
          animate: {
            opacity: [0, 1, 1, 1],
            scale: [scaleIn, 1, 1, 1],
            y: [amountOfMovement, amountOfMovement, 0, 0],
            boxShadow: [CARD_SHADOW_OFF, CARD_SHADOW_ON, CARD_SHADOW_ON, CARD_SHADOW_OFF]
          },
          exit: {
            opacity: [1, 0.7, 0.7, 0],
            y: [0, -amountOfMovement, 0, 0],
            scale: [1, scaleOut, scaleOut, scaleOut],
            boxShadow: CARD_SHADOW_OFF
          }
        },
        initial: 'initial',
        animate: 'animate',
        exit: 'exit'
      }
    }, [shouldReduceMotion])

    const overlayQuickExit = useMemo(
      () => ({
        opacity: 0,
        boxShadow: CARD_SHADOW_OFF,
        transition: shouldReduceMotion
          ? { duration: 0.08, ease: 'linear' as const }
          : { duration: 0.14, ease: 'easeOut' as const }
      }),
      [shouldReduceMotion]
    )

    return (
      <StyledColumnLayoutColumn
        $fullWidth={!(isRight && isOverlay)}
        $isRight={isRight}
        $isOverlayMode={isOverlay}
      >
        <StyledColumnLayoutColumnContentWrapper>
          <AnimatePresence initial={false}>
            {shouldBeVisible(isVisible) && (
              <StyledColumnLayoutColumnContent
                key={innerKey}
                ref={setContentRef}
                {...(floatingCardLayout ? { style: { ...floatingCardLayout } } : {})}
                {...(isRight ? animation : {})}
                exit={
                  isRight ? (isOverlayCardVisible ? overlayQuickExit : animation.exit) : undefined
                }
                $isOverlayMode={isOverlay}
                $isRight={isRight}
                {...rest}
                $isMobile={isMobile}
              >
                <FloatingDetailControlsContext.Provider
                  value={{
                    isOverlayCardVisible,
                    onClose,
                    registerCustomCloseButton
                  }}
                >
                  {shouldRenderFallbackCloseButton && (
                    <StyledFloatingDetailHeader>
                      <StyledFloatingDetailCloseButton
                        type="button"
                        aria-label="Sulje"
                        onClick={onClose}
                      >
                        <FaXmark />
                      </StyledFloatingDetailCloseButton>
                    </StyledFloatingDetailHeader>
                  )}
                  {isOverlayCardVisible ? (
                    <StyledFloatingDetailBody>{children}</StyledFloatingDetailBody>
                  ) : (
                    children
                  )}
                </FloatingDetailControlsContext.Provider>
              </StyledColumnLayoutColumnContent>
            )}
          </AnimatePresence>
        </StyledColumnLayoutColumnContentWrapper>
      </StyledColumnLayoutColumn>
    )
  }
)
